// src/stores/useAppointmentCancellationStore.ts
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { useApiLoading } from '../lib/composables/useApiLoading';
import {
  cancelAppointmentApi,
  fetchAppointmentCancellationApi,
} from '../services/appointments.service';
import type { AppointmentSummary } from '../types/appointment';

// Access is the link alone — no identity challenge; the endpoint gates on the
// appointment's own state. Also used by booked-successfully.
export const useAppointmentCancellationStore = defineStore('appointment-cancellation', () => {
  const { apiLoading, setApiLoading, setApiDone } = useApiLoading();

  const appointmentId = ref<string | null>(null);
  const appointment = ref<AppointmentSummary | null>(null);
  const confirmed = ref(false);
  const isCancelled = ref(false);
  /** A state of this page, not a request failure — hence not in the error store. */
  const cannotCancelReason = ref<string | null>(null);

  const canCancel = computed(() => appointment.value !== null && cannotCancelReason.value === null);

  function reset() {
    appointment.value = null;
    confirmed.value = false;
    isCancelled.value = false;
    cannotCancelReason.value = null;
  }

  function setConfirmed(value: boolean) {
    confirmed.value = value;
  }

  async function load(id: string) {
    if (apiLoading.value) return { ok: false };

    appointmentId.value = id;
    reset();
    setApiLoading();

    try {
      const response = await fetchAppointmentCancellationApi(id);

      if (!response.ok || !response.data) {
        return { ok: false, errors: response.errors };
      }

      const { startDateTime, deliveryMode, location, practitioner } = response.data;

      // `startDateTime` becomes `dateTime` here and stays that on every screen.
      appointment.value = { dateTime: startDateTime, deliveryMode, location, practitioner };
      cannotCancelReason.value = response.data.canCancel ? null : response.data.cannotCancelReason;

      return { ok: true };
    } finally {
      setApiDone();
    }
  }

  async function cancel() {
    if (apiLoading.value || appointmentId.value === null) return { ok: false };

    setApiLoading();

    try {
      const response = await cancelAppointmentApi(appointmentId.value);

      if (!response.ok) {
        return { ok: false, errors: response.errors };
      }

      isCancelled.value = true;

      return { ok: true };
    } finally {
      setApiDone();
    }
  }

  return {
    apiLoading,
    appointment,
    appointmentId,
    cannotCancelReason,
    confirmed,
    isCancelled,

    // getters
    canCancel,

    // actions
    cancel,
    load,
    reset,
    setConfirmed,
  };
});
